import { FC, useMemo, useState } from 'react'
import { Control, useController } from 'react-hook-form'
import {
	Badge,
	Box,
	Button,
	ExpandableSection,
	SpaceBetween
} from '@cloudscape-design/components'

import AccessSummaryCell from '../AccessSummaryCell'
import FormMultiselect from '../../ControlledFormInputs/Multiselect'
import FormTokenGroup from '../../ControlledFormInputs/InputTokenGroup'
import {
	RACI_ROLE_OPTION_GROUPS,
	TAG_APPROVER_TEAM_PERMISSION_PRESETS,
	buildPermissionsPayload,
	expandRaciRolesToPermissions,
	normalizeRaciRoleSelection,
	toCustomPermissionToken
} from '../../../helper/permissionRoles'

const EMPTY_RACI_ROLES: Array<{ label: string; value: string }> = []
const EMPTY_CUSTOM_PERMISSIONS: Array<{
	label: string
	dismissLabel: string
	disabled?: boolean
}> = []
const PREVIEW_BADGE_LIMIT = 8

type RaciRoleSelectorProps = {
	control: Control<any>
	setFormValues: (fieldName: string, value: any) => void
	raciRoles?: Array<{ label: string; value: string }>
	customPermissions?: Array<{ label: string; dismissLabel: string; disabled?: boolean }>
	existingPermissions?: string[]
	hasExistingAccess?: boolean
	showCustomPermissions?: boolean
	readOnly?: boolean
}

const PermissionBadgeList: FC<{
	permissions: string[]
	limit?: number
	color?: 'blue' | 'green' | 'grey'
}> = ({ permissions, limit = PREVIEW_BADGE_LIMIT, color = 'grey' }) => {
	const visible = permissions.slice(0, limit)
	const hiddenCount = Math.max(0, permissions.length - limit)

	return (
		<div
			style={{
				display: 'flex',
				flexWrap: 'wrap',
				gap: '6px',
				alignItems: 'center'
			}}
		>
			{visible.map((permission) => (
				<Badge key={permission} color={color}>
					<code>{permission}</code>
				</Badge>
			))}
			{hiddenCount > 0 && <Badge color="grey">+{hiddenCount} more</Badge>}
		</div>
	)
}

const RaciRoleSelector: FC<RaciRoleSelectorProps> = ({
	control,
	setFormValues,
	raciRoles = EMPTY_RACI_ROLES,
	customPermissions = EMPTY_CUSTOM_PERMISSIONS,
	existingPermissions = [],
	hasExistingAccess = false,
	showCustomPermissions = false,
	readOnly = false
}) => {
	const [showAllCustom, setShowAllCustom] = useState(false)

	const { field: customPermissionsField } = useController({
		name: 'customPermissions',
		control,
		defaultValue: customPermissions
	})

	const assignedCustomPermissionLabels = useMemo(
		() => new Set(customPermissions.map((permission) => permission.label)),
		[customPermissions]
	)

	const addTagApproverPreset = (permission: string) => {
		if (assignedCustomPermissionLabels.has(permission)) {
			return
		}

		const updated = [...customPermissions, toCustomPermissionToken(permission)]
		customPermissionsField.onChange(updated)
		setFormValues('customPermissions', updated)
	}

	const raciMappedPermissions = useMemo(
		() => expandRaciRolesToPermissions(raciRoles),
		[raciRoles]
	)

	const customPermissionLabels = useMemo(
		() => customPermissions.map((permission) => permission.label),
		[customPermissions]
	)

	const hasCustomPermissions = customPermissionLabels.length > 0
	const hasRaciSelection = raciRoles.length > 0

	if (readOnly) {
		return (
			<SpaceBetween direction="vertical" size="m">
				<SpaceBetween direction="vertical" size="xs">
					<Box variant="awsui-key-label">RACI roles</Box>
					{hasRaciSelection ? (
						<div
							style={{
								display: 'flex',
								flexWrap: 'wrap',
								gap: '6px'
							}}
						>
							{raciRoles.map((role) => (
								<Badge key={role.value} color="green">
									{role.label}
								</Badge>
							))}
						</div>
					) : (
						<Box color="text-body-secondary">No RACI roles assigned</Box>
					)}
				</SpaceBetween>

				{hasRaciSelection && raciMappedPermissions.length > 0 && (
					<SpaceBetween direction="vertical" size="xs">
						<Box variant="awsui-key-label">Permissions from RACI roles</Box>
						<PermissionBadgeList permissions={raciMappedPermissions} color="green" />
					</SpaceBetween>
				)}

				{hasCustomPermissions && (
					<ExpandableSection
						headerText={`Additional permissions (${customPermissionLabels.length})`}
						defaultExpanded={customPermissionLabels.length <= 5}
					>
						<PermissionBadgeList
							permissions={customPermissionLabels}
							limit={customPermissionLabels.length}
							color="blue"
						/>
					</ExpandableSection>
				)}

				<SpaceBetween direction="vertical" size="xs">
					<Box variant="awsui-key-label">Effective access</Box>
					<AccessSummaryCell
						permissions={
							existingPermissions.length > 0
								? existingPermissions
								: buildPermissionsPayload(raciRoles, customPermissions)
						}
					/>
				</SpaceBetween>
			</SpaceBetween>
		)
	}

	return (
		<SpaceBetween direction="vertical" size="l">
			<FormMultiselect
				name="raciRoles"
				label="RACI roles"
				description="Select standardized per-domain roles. Permissions are mapped automatically."
				control={control}
				optionGroups={RACI_ROLE_OPTION_GROUPS}
				placeholder="Select RACI roles"
				normalizeSelectedOptions={normalizeRaciRoleSelection}
				rules={{
					validate: (_value, formValues) => {
						const resolved = buildPermissionsPayload(
							formValues.raciRoles,
							formValues.customPermissions
						)
						return (
							resolved.length > 0 ||
							hasExistingAccess ||
							'Select at least one RACI role or permission'
						)
					}
				}}
				setFormValues={setFormValues}
			/>

			<SpaceBetween direction="vertical" size="xs">
				<Box variant="awsui-key-label">Permissions from selected roles</Box>
				{hasRaciSelection ? (
					<PermissionBadgeList
						permissions={raciMappedPermissions}
						color="green"
					/>
				) : hasExistingAccess ? (
					<>
						<Box color="text-body-secondary">
							Current access summary (select RACI roles to remap):
						</Box>
						<AccessSummaryCell permissions={existingPermissions} />
					</>
				) : (
					<Box color="text-body-secondary">
						Select a RACI role to preview mapped permissions
					</Box>
				)}
			</SpaceBetween>

			{hasCustomPermissions && !showCustomPermissions && (
				<ExpandableSection
					headerText={`Additional permissions not mapped to RACI (${customPermissionLabels.length})`}
					defaultExpanded={false}
				>
					<SpaceBetween direction="vertical" size="xs">
						<Box color="text-body-secondary">
							These legacy or custom permissions are kept on save unless removed
							by a superadmin.
						</Box>
						{showAllCustom ? (
							<PermissionBadgeList
								permissions={customPermissionLabels}
								limit={customPermissionLabels.length}
								color="blue"
							/>
						) : (
							<>
								<PermissionBadgeList
									permissions={customPermissionLabels}
									limit={5}
									color="blue"
								/>
								{customPermissionLabels.length > 5 && (
									<Button variant="inline-link" onClick={() => setShowAllCustom(true)}>
										Show all {customPermissionLabels.length} permissions
									</Button>
								)}
							</>
						)}
					</SpaceBetween>
				</ExpandableSection>
			)}

			{showCustomPermissions && (
				<SpaceBetween direction="vertical" size="m">
					<SpaceBetween direction="vertical" size="xs">
						<Box variant="awsui-key-label">Tag approval team presets</Box>
						<div
							style={{
								display: 'flex',
								flexWrap: 'wrap',
								gap: '8px'
							}}
						>
							{TAG_APPROVER_TEAM_PERMISSION_PRESETS.map((preset) => {
								const isAssigned = assignedCustomPermissionLabels.has(
									preset.permission
								)

								return (
									<Button
										key={preset.permission}
										variant={isAssigned ? 'normal' : 'inline-link'}
										disabled={isAssigned}
										onClick={() => addTagApproverPreset(preset.permission)}
									>
										{isAssigned ? `${preset.label} added` : `+ ${preset.label}`}
									</Button>
								)
							})}
						</div>
					</SpaceBetween>

					<FormTokenGroup
						key={`custom-permissions-${customPermissions.length}`}
						label="Advanced custom permissions"
						description="Superadmin only. Add permissions outside the RACI catalog."
						name="customPermissions"
						control={control}
						items={customPermissions}
						defaultValue={customPermissions}
						placeholder="Type permission and press Enter"
						setFormValues={setFormValues}
					/>
				</SpaceBetween>
			)}
		</SpaceBetween>
	)
}

export default RaciRoleSelector
